import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { Api } from './api';

export interface User {
  phone: string;
  name?: string;
  primaryPersona?: string;
  personas?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class Auth {
  private api = inject(Api);
  private router = inject(Router);
  
  private tokenSignal = signal<string | null>(this.getStoredToken());
  private userSignal = signal<User | null>(this.getStoredUser());

  token = this.tokenSignal.asReadonly();
  user = this.userSignal.asReadonly();
  isAuthenticated = computed(() => !!this.tokenSignal());

  constructor() {
    // Check if token is expired on initialization
    const token = this.tokenSignal();
    if (token && this.isTokenExpired(token)) {
      this.logout();
    }
  }

  requestOTP(phone: string): Observable<{ message: string; expiresIn: number }> {
    return this.api.requestOTP(phone);
  }

  verifyOTP(phone: string, otp: string): Observable<{ token: string; user: User }> {
    return this.api.verifyOTP(phone, otp).pipe(
      tap((response: { token: string; user: User }) => {
        this.setAuth(response.token, response.user);
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  logout(): void {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    this.tokenSignal.set(null);
    this.userSignal.set(null);
    this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return this.tokenSignal();
  }

  getUser(): User | null {
    return this.userSignal();
  }

  private setAuth(token: string, user: User): void {
    localStorage.setItem('admin_token', token);
    localStorage.setItem('admin_user', JSON.stringify(user));
    this.tokenSignal.set(token);
    this.userSignal.set(user);
  }

  private getStoredToken(): string | null {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('admin_token');
    }
    return null;
  }

  private getStoredUser(): User | null {
    if (typeof window !== 'undefined') {
      const userStr = localStorage.getItem('admin_user');
      if (userStr) {
        try {
          return JSON.parse(userStr);
        } catch {
          return null;
        }
      }
    }
    return null;
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= exp;
    } catch {
      return true;
    }
  }
}
